// src/hooks/useAIEstimate.js - Hook for AI-powered food estimation
import { useState, useCallback } from 'react';
import { aiService } from '../services/aiService';

/**
 * Custom hook for AI estimation using Mistral AI (with Groq fallback)
 * Provides state management for AI-powered food estimation
 * @returns {Object} AI estimation state and methods
 */
export const useAIEstimate = () => {
  const [isEstimating, setIsEstimating] = useState(false);
  const [error, setError] = useState(null);
  const [estimatedFood, setEstimatedFood] = useState(null);
  const [showEstimation, setShowEstimation] = useState(false);

  /**
   * Check if AI service is available
   * @returns {Object} Service status
   */
  const getServiceStatus = useCallback(() => {
    return aiService.getServiceStatus();
  }, []);

  /**
   * Estimate food nutritional values using AI
   * @param {string} foodName - Name of the food to estimate
   * @returns {Promise<Object|null>} Estimated food data or null on error
   */
  const estimateFood = useCallback(async (foodName) => {
    if (!foodName || !foodName.trim()) {
      setError('Veuillez entrer un nom d\'aliment');
      return null;
    }

    // Check if service is available
    const status = aiService.getServiceStatus();
    if (!status.available) {
      setError(status.message);
      return null;
    }

    setIsEstimating(true);
    setError(null);
    setEstimatedFood(null);

    try {
      console.log('🤖 Estimation IA pour:', foodName);
      const result = await aiService.estimateFood(foodName);

      // Validate estimation
      const validation = aiService.validateEstimation(result);
      if (validation.warnings.length > 0) {
        console.warn('⚠️ Avertissements de validation:', validation.warnings);
      }

      // Add validation warnings to result
      const finalResult = {
        ...result,
        validation: validation
      };

      console.log('✅ Estimation réussie:', finalResult);
      setEstimatedFood(finalResult);
      setShowEstimation(true);
      return finalResult;

    } catch (err) {
      console.error('❌ Erreur estimation IA:', err);
      const errorMessage = err.message || 'Erreur lors de l\'estimation IA';
      setError(errorMessage);
      return null;

    } finally {
      setIsEstimating(false);
    }
  }, []);

  /**
   * Clear estimation results
   */
  const clearEstimation = useCallback(() => {
    setEstimatedFood(null);
    setError(null);
    setShowEstimation(false);
  }, []);

  /**
   * Retry last estimation
   * @param {string} foodName - Food name to retry
   */
  const retryEstimation = useCallback(async (foodName) => {
    clearEstimation();
    return await estimateFood(foodName);
  }, [clearEstimation, estimateFood]);

  /**
   * Add estimated food to database
   * @param {Function} addFoodCallback - Callback to add food to database
   * @returns {boolean} Success status
   */
  const addEstimatedFood = useCallback((addFoodCallback) => {
    if (!estimatedFood) {
      setError('Aucune estimation à ajouter');
      return false;
    }

    if (typeof addFoodCallback !== 'function') {
      setError('Fonction d\'ajout invalide');
      return false;
    }

    try {
      // Call the add food function with estimated data
      const success = addFoodCallback({
        name: estimatedFood.name,
        gi: estimatedFood.gi,
        carbs: estimatedFood.carbs,
        source: estimatedFood.source,
        confidence: estimatedFood.confidence,
        explanation: estimatedFood.explanation,
        estimatedBy: estimatedFood.estimatedBy,
        warning: estimatedFood.warning
      });

      if (success) {
        clearEstimation();
        return true;
      } else {
        setError('Impossible d\'ajouter l\'aliment');
        return false;
      }
    } catch (err) {
      setError(`Erreur lors de l'ajout: ${err.message}`);
      return false;
    }
  }, [estimatedFood, clearEstimation]);

  return {
    // State
    isEstimating,
    error,
    estimatedFood,
    showEstimation,
    isAvailable: aiService.isServiceAvailable(),

    // Methods
    estimateFood,
    clearEstimation,
    retryEstimation,
    addEstimatedFood,
    getServiceStatus
  };
};

export default useAIEstimate;
